import { Component, OnInit, ViewChild } from '@angular/core';
import { LookupService } from '../../../../services/lookup.service';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { FormsModule } from '@angular/forms';
import { BillingService } from '../../../../services/billing.service';
import { ChartComponent, NgApexchartsModule } from 'ng-apexcharts';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { UserService } from '../../../../services/user.service';
import { CommonModule } from '@angular/common';
import { ListTableComponent } from '../../../../shared/components/list-table/list-table.component';
import { ReportFiltersComponent } from '../report-filters/report-filters.component';

@Component({
  selector: 'ppl-denial-report',
  standalone: true,
  imports: [
    NzSelectModule,
    FormsModule,
    NgApexchartsModule,
    NzCardModule,
    NzRadioModule,
    NzGridModule,
    CommonModule,
    ListTableComponent,
    ReportFiltersComponent,
  ],
  templateUrl: './denial-report.component.html',
  styleUrl: './denial-report.component.css',
})
export class DenialReportComponent implements OnInit {
  mcoList: any;
  programName: any;
  mcoName: any = '';
  selectedClaimType: any = 'All';
  claimTypes = ['All', 'SC', 'PMPM'];
  denialReportData: any;
  denialReportGridData: any;
  chartOptions: any;
  categories: any;
  displayBy = 'count';
  metricType = 'code';
  dataToDisplay: any = [];
  cols: any;
  title: any;
  filterFields = [
    { field: 'displayBy', show: true, default: 'count' },
    { field: 'metricType', show: true, default: 'code' },
    { field: 'mcoName', show: true, default: '' },
    { field: 'selectedClaimType', show: true, default: 'All' },
  ];

  @ViewChild('chart') chart!: ChartComponent;

  constructor(
    private lookupService: LookupService,
    private billingService: BillingService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.lookupService.getOrgData(this.programName).subscribe((data: any) => {
      if (data.status === 200) {
          this.mcoList = data.responsedata.filter((item: any) => item.orgTypeLookup === 'MCO');
          this.mcoName = this.mcoList[0]?.orgName;
          this.filterFields = this.filterFields?.map(f =>
            f.field === 'mcoName' ? { ...f, default: this.mcoList[0]?.orgName } : f
          ) ?? [];
        this.getDenialData(this.mcoName, this.selectedClaimType);
      }
    });
  }

  filterChanged(change: any) {
    this.mcoName = change.formValues.mcoName;
    this.selectedClaimType = change.formValues.selectedClaimType;
    this.metricType = change.formValues.metricType;
    this.displayBy = change.formValues.displayBy;
    if (change.field === 'mcoName' || change.field === 'selectedClaimType') {
      this.onSearchChange();
    } else if (change.field === 'metricType') {
      this.getDenialData(this.mcoName, this.selectedClaimType);
      this.setCategories();
    } else {
      this.onDisplayByChange();
    }
  }

  getTableCols() {
    const isCode = this.metricType === 'code';
    const isAmount = this.displayBy === 'amount';
    const valueCol = {
      field: isAmount ? 'amount' : 'count',
      header: isAmount ? 'Amount' : 'Count',
      jsonpath: isAmount ? 'amount' : 'count',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    };
    const typeCol = {
      field: isCode ? 'carcRarc' : 'mcoName',
      header: isCode ? 'CARC-RARC' : 'MCO',
      jsonpath: isCode ? 'carcRarc' : 'mcoName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    };

    this.cols = [typeCol, valueCol];
  }

  getTotal(type: string): string {
    const total = this.denialReportData?.reduce((sum: any, item: any) => {
      const value = +item[type] || 0;
      return sum + value;
    }, 0);

    return type === 'amount'
      ? `$${total.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : total.toLocaleString();
  }

  fetchChartOptions() {
    this.getTableCols();
    this.chartOptions = {
      series: [
        {
          name: 'Count',
          data: this.dataToDisplay,
        },
      ],
      chart: {
        type: 'bar',
        height: 400,
        fontFamily: 'inherit',
      },
      title: {
        text: `${this.title}: ${this.getTotal(this.displayBy)}`,
        align: 'right',
        margin: 25,
        offsetY: 20,
      },
      noData: {
        text: 'No data available',
        align: 'center',
        verticalAlign: 'middle',
        style: {
          color: '#999',
          fontSize: '16px',
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '40%',
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      xaxis: {
        categories: this.categories,
        title: {
          text: this.metricType === 'mco' ? 'MCO' : 'CARC-RARC',
          style: {
            fontSize: '12px',
          },
        },
        labels: {
          show: true,
          rotate: -45,
          hideOverlappingLabels: false,
        },
      },
      yaxis: {
        title: {
          text: this.displayBy === 'amount' ? 'Amount' : 'Count',
          style: {
            fontSize: '12px',
          },
        },
      },
      fill: {
        opacity: 1,
      },
      tooltip: {
        enabled: true,
        followCursor: true,
        custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
          if (this.metricType !== 'code') {
            return null;
          }
          const item = this.denialReportData?.[dataPointIndex];
          return `
            <div style="
              padding: 16px;
              max-width: 350px;
              white-space: normal;
              word-wrap: break-word;
              transform: translateX(12px);
            ">
              <strong>CARC:</strong> ${item?.carc}<br/>
              <strong>RARC:</strong> ${this.formatRarc(item?.rarc)}<br/>
            </div>
          `;
        },
      },
      legend: {},
      responsive: [
        {
          breakpoint: 768, // applies when screen width <= 768px
          options: {
            chart: {
              height: 300,
            },
            legend: {
              position: 'bottom',
            },
            xaxis: {
              labels: {
                rotate: -45,
                style: {
                  fontSize: '10px',
                },
              },
            },
            dataLabels: {
              style: {
                fontSize: '6px',
              },
            },
          },
        },
      ],
    };
  }

  formatRarc(rarc: string): string {
    if (!rarc) return '';
    return rarc
      .split(';')
      .map((item) => item.trim())
      .join('<br/>');
  }

  onSearchChange() {
    this.getDenialData(this.mcoName, this.selectedClaimType);
  }

  onDisplayByChange() {
    if (this.displayBy === 'amount') {
      this.title = 'Total Denied Amount';
      this.dataToDisplay = this.denialReportData?.map(
        (item: any) => item.amount
      );
    } else {
      this.title = 'Total Denied Count';
      this.dataToDisplay = this.denialReportData?.map(
        (item: any) => item.count
      );
    }
    this.setCategories();
    this.fetchChartOptions();
  }

  onMetricChange() {
    this.getDenialData(this.mcoName, this.selectedClaimType);
    this.setCategories();
  }

  setCategories() {
    if (this.metricType === 'code') {
      this.categories = this.denialReportData?.map(
        (item: any) => `${item.carcCode} - ${item.rarcCode}`
      );
    } else {
      this.categories = this.denialReportData?.map((item: any) => item.mcoName);
    }
  }

  getDenialData(mco: string, claimType: string) {
    const options = {
      reportName: 'fetchDeniedReport',
      mcoName: mco,
      claimType: claimType,
      programCode: this.programName,
      splitBy: this.metricType?.toUpperCase(),
    };
    this.billingService.getReports(options).subscribe((data: any) => {
      this.denialReportData = data.responsedata || [];
      if (this.denialReportData?.length) {
        this.denialReportData = this.denialReportData.map((item: any) => ({
          ...item,
          carcRarc: `${item.carcCode} - ${item.rarcCode}`,
        }));
      }
      this.denialReportGridData = JSON.parse(
        JSON.stringify(this.denialReportData)
      );
      this.denialReportGridData.forEach((element: any) => {
        element.amount = element.amount
          ? this.billingService.formatCurrency(element.amount)
          : element.amount;
      });
      this.onDisplayByChange();
      this.fetchChartOptions();
    });
  }
}
